import { useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, Clock, CheckCircle2, Zap, Calendar, Shield, ArrowRight } from "lucide-react";

export default function StepPaymentChoice({ formData, setFormData, onPayNow, onPayLater, isGroup, totalAmount, numberOfAttendees }) {
  const [selectedChoice, setSelectedChoice] = useState(
    formData?.paymentChoice || null
  );

  const handleChoice = (choice) => {
    if (choice === "now") {
      setSelectedChoice(choice);
      setFormData?.((prev) => ({ ...prev, paymentChoice: choice }));
      return;
    }
    setSelectedChoice(choice);
    setFormData?.((prev) => ({ ...prev, paymentChoice: choice }));
  };

  const handleProceed = () => {
    if (selectedChoice === "now") {
      onPayNow?.();
    } else if (selectedChoice === "later") {
      onPayLater?.();
    }
  };

  const features = {
    now: [
      "Instant confirmation and ticket delivery",
      "Secure payment processing",
      "Priority support and assistance",
      "Immediate access to event materials",
    ],
    later: [
      "Reserve your spot without payment",
      "Payment link sent to your email",
      "Flexible payment deadline",
      "Pay at your convenience",
    ],
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="mx-auto max-w-5xl"
    >
      {/* Header Section */}
      <div className="mb-8 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          className="inline-flex justify-center items-center mb-4 w-16 h-16 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-xl"
        >
          <CreditCard size={32} />
        </motion.div>
        <h2 className="mb-2 text-3xl font-bold text-gray-900">Choose Your Payment Option</h2>
        <p className="text-base text-gray-600">
          Select how you'd like to complete your registration payment
        </p>
      </div>

      {/* Success Confirmation Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex gap-3 items-center p-4 mb-8 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg"
      >
        <div className="flex justify-center items-center w-10 h-10 bg-white rounded-full shrink-0">
          <CheckCircle2 size={20} className="text-green-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white">
            {isGroup ? "Group Registration Submitted Successfully! 🎉" : "Registration Submitted Successfully! 🎉"}
          </p>
          <p className="text-xs text-green-100">
            {isGroup && numberOfAttendees != null && totalAmount != null
              ? `${numberOfAttendees} attendee(s) · Total: ${totalAmount === 0 ? "FREE" : `GH₵ ${Number(totalAmount).toFixed(2)}`}`
              : "Now, let's complete your payment to secure your spot"}
          </p>
        </div>
      </motion.div>

      {/* Payment Options */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        {/* Pay Now Option - Enabled when backend supports; for now allow click to proceed */}
        <motion.button
          type="button"
          onClick={() => handleChoice("now")}
          className={`relative w-full p-8 text-left rounded-2xl border-2 transition-all ${
            selectedChoice === "now"
              ? "bg-gradient-to-br from-orange-50 via-orange-100 to-orange-50 border-orange-500 shadow-2xl"
              : "bg-white border-gray-200 hover:border-orange-300 hover:shadow-xl"
          }`}
        >

          {/* Icon */}
          <div className={`inline-flex justify-center items-center mb-4 w-14 h-14 rounded-xl ${
            selectedChoice === "now" ? "bg-orange-500 text-white" : "bg-gray-200"
          }`}>
            <CreditCard size={24} className={selectedChoice === "now" ? "text-white" : "text-gray-400"} />
          </div>

          {/* Title */}
          <h3 className="mb-2 text-2xl font-bold text-gray-900">Pay Now</h3>
          <p className="mb-4 text-sm text-gray-600">Complete your payment immediately and get instant confirmation</p>

          {/* Features */}
          <ul className="mb-6 space-y-2">
            {features.now.map((feature, index) => (
              <li
                key={index}
                className="flex gap-2 items-start text-sm text-gray-700"
              >
                <CheckCircle2 size={16} className="mt-0.5 text-orange-500 shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          {selectedChoice === "now" && (
            <div className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-orange-700 bg-orange-200 rounded-lg">
              <CheckCircle2 size={16} />
              Selected
            </div>
          )}
        </motion.button>

        {/* Pay Later Option */}
        <motion.button
          type="button"
          onClick={() => handleChoice("later")}
          whileHover={{ scale: 1.02, y: -4 }}
          whileTap={{ scale: 0.98 }}
          className={`relative p-8 text-left rounded-2xl border-2 transition-all ${
            selectedChoice === "later"
              ? "bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 border-blue-500 shadow-2xl"
              : "bg-white border-gray-200 hover:border-blue-300 hover:shadow-xl"
          }`}
        >
          {/* Icon */}
          <div className={`inline-flex justify-center items-center mb-4 w-14 h-14 rounded-xl ${
            selectedChoice === "later"
              ? "bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg"
              : "bg-gray-100"
          }`}>
            <Clock size={24} className={selectedChoice === "later" ? "text-white" : "text-gray-400"} />
          </div>

          {/* Title */}
          <h3 className="mb-2 text-2xl font-bold text-gray-900">Pay Later</h3>
          <p className="mb-4 text-sm text-gray-600">Reserve your spot now and complete payment at your convenience</p>

          {/* Features */}
          <ul className="mb-6 space-y-2">
            {features.later.map((feature, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-2 items-start text-sm text-gray-700"
              >
                <CheckCircle2 size={16} className="mt-0.5 text-blue-500 shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>

          {/* Selection Indicator */}
          {selectedChoice === "later" && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-blue-700 bg-blue-200 rounded-lg"
            >
              <CheckCircle2 size={16} />
              Selected
            </motion.div>
          )}
        </motion.button>
      </div>

      {/* Info Boxes */}
      <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2">
        <div className="flex gap-3 items-start p-4 bg-gradient-to-br from-teal-50 to-white rounded-xl border border-teal-200">
          <Shield size={20} className="mt-0.5 text-teal-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-teal-900">Secure Payment</p>
            <p className="text-xs text-teal-700">All payments are processed securely with industry-standard encryption</p>
          </div>
        </div>

        <div className="flex gap-3 items-start p-4 bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-200">
          <Calendar size={20} className="mt-0.5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Payment Deadline</p>
            <p className="text-xs text-amber-700">If paying later, complete payment within 7 days to secure your spot</p>
          </div>
        </div>
      </div>

      {/* Proceed Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center"
      >
        <motion.button
          onClick={handleProceed}
          disabled={!selectedChoice}
          whileHover={selectedChoice ? { scale: 1.05 } : {}}
          whileTap={selectedChoice ? { scale: 0.95 } : {}}
          className={`inline-flex gap-3 items-center px-10 py-4 text-lg font-bold text-white rounded-xl shadow-lg transition-all ${
            selectedChoice === "later"
              ? "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
              : selectedChoice === "now"
              ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          {selectedChoice === "later" ? "Complete Registration (Pay Later)" : selectedChoice === "now" ? "Proceed to Payment" : "Select an option to continue"}
          <ArrowRight size={20} />
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

